import { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API_BASE, getToken } from "@/lib/api";
import { toast } from "sonner";

/**
 * Image upload widget for admin.
 * - Uploads to POST /api/admin/uploads/image
 * - Returns the permanent URL via onChange
 * - Also lets admin paste a URL manually
 */
export default function ImageUpload({ value, onChange, label = "Image URL", folder = "branding", testId = "image-upload", helper }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folder);
      const res = await fetch(`${API_BASE}/admin/uploads/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {label && <div className="text-sm font-medium leading-none text-slate-900">{label}</div>}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Paste URL or click Upload"
          data-testid={`${testId}-url`}
          className="flex-1"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,.ico"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-button`}
          className="gap-1.5"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </div>
      {helper && <p className="text-xs text-slate-500">{helper}</p>}
      {value && (
        <div className="mt-2 flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <img src={value} alt="Preview" className="h-14 w-14 rounded-md object-cover" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs text-slate-600">{value}</div>
          </div>
        </div>
      )}
    </div>
  );
}
